import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CountrySelect,
  DatePicker,
  Descriptions,
  Field,
  Icon,
  Input,
  NumberInput,
  Progress,
  Select,
  Space,
  Spin,
  Switch,
  Tabs,
  Tag,
  Text,
  Textarea,
  Title,
  useToast,
} from "@app/ui";
import type { GroupItemField, PassportField } from "@app/dpp-schemas";
import { getProduct, updateProduct } from "../api/dpp.js";
import { createLatestGate } from "../api/latest.js";
import { useCan } from "../auth/useCan.js";
import { ErrorAlert } from "../errors/index.js";
import { useLang, useT, useFormatDateTime } from "../i18n.js";
import { NoOrgState } from "../orgs/NoOrgState.js";
import { useOrgs } from "../orgs/OrgContext.js";
import { fieldLabel, getSchemaDef } from "../dpp/schema-registry.js";
import { LcaCard } from "../dpp/LcaCard.js";
import { MediaCard } from "../dpp/MediaCard.js";
import { ComponentsCard, SupplyChainCard } from "../dpp/SupplyChainCards.js";
import { SupplierInvitesCard } from "../dpp/SupplierInvitesCard.js";
import { ProductOperatorsCard } from "../dpp/ProductOperatorsCard.js";
import { DocumentsCard } from "../dpp/DocumentsCard.js";
import { ProductCustomsCodes } from "../dpp/ProductCustomsCodes.js";
import { ProductIdentifiersPanel } from "../dpp/ProductIdentifiersPanel.js";
import type { ProductDetail, ProductStatus } from "../types.js";

const STATUS_COLOR: Record<ProductStatus, "default" | "success" | "warning"> = {
  draft: "warning",
  active: "success",
  archived: "default",
};

/**
 * Form values: canonical decimal strings for numeric fields, booleans for
 * switches, and per-row string drafts for group fields (numbers/booleans are
 * stringified while editing and converted back on save).
 */
type GroupRowDraft = Record<string, string>;
type FormValue = string | boolean | GroupRowDraft[];

/** A form draft counts as "holding data" when it is a non-blank string, a set switch or a non-empty group. */
function hasFormValue(value: FormValue | undefined): boolean {
  if (value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  return value === true;
}

function groupRowDraft(itemFields: readonly GroupItemField[], raw: unknown): GroupRowDraft {
  const source = typeof raw === "object" && raw !== null ? (raw as Record<string, unknown>) : {};
  const row: GroupRowDraft = {};
  for (const item of itemFields) {
    const v = source[item.key];
    if (item.type === "boolean") row[item.key] = v === true ? "true" : v === false ? "false" : "";
    else if (typeof v === "number" && Number.isFinite(v)) row[item.key] = String(v);
    else row[item.key] = typeof v === "string" ? v : "";
  }
  return row;
}

function initialFormValue(field: PassportField, raw: unknown): FormValue {
  if (field.type === "group") {
    return Array.isArray(raw) ? raw.map((r) => groupRowDraft(field.itemFields ?? [], r)) : [];
  }
  if (field.type === "boolean") return raw === true;
  if (field.type === "number" || field.type === "percentage") {
    return typeof raw === "number" && Number.isFinite(raw) ? String(raw) : "";
  }
  return typeof raw === "string" ? raw : "";
}

/** Group tables and prose paragraphs span the full form row; everything else sits in the 2-col grid. */
function isWideField(field: PassportField): boolean {
  return (
    field.type === "group" || field.presentation?.kind === "summary" || field.presentation?.kind === "prose"
  );
}

export function ProductDetailPage() {
  const t = useT();
  const { lang } = useLang();
  const fmtDate = useFormatDateTime();
  const toast = useToast();
  const can = useCan();
  const { org, loading: orgsLoading } = useOrgs();
  const { id } = useParams<{ id: string }>();

  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [form, setForm] = useState<Record<string, FormValue>>({});
  const [edited, setEdited] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const canWrite = can("product:write");
  const canManageSuppliers = can("supplier:manage");
  const def = product ? getSchemaDef(product.schemaId) : undefined;

  /** (Re)builds the form drafts from a product's stored attrs and clears the dirty set. */
  const initForm = (detail: ProductDetail) => {
    const schema = getSchemaDef(detail.schemaId);
    if (!schema) return;
    const next: Record<string, FormValue> = {};
    for (const category of schema.categories) {
      for (const field of category.fields) {
        next[field.id] = initialFormValue(field, detail.attrs[field.id]);
      }
    }
    setForm(next);
    setEdited(new Set());
  };

  // Switching org/product mid-load must not let the older response land last.
  const gate = useRef(createLatestGate()).current;

  const load = async (orgId: string, productId: string) => {
    const token = gate.begin();
    setError(null);
    try {
      const detail = await getProduct(orgId, productId);
      if (!gate.isCurrent(token)) return;
      setProduct(detail);
      initForm(detail);
    } catch (e) {
      if (gate.isCurrent(token)) setError(e);
    }
  };

  useEffect(() => {
    if (org && id) void load(org.id, id);
    return () => gate.cancel();
  }, [org?.id, id]);

  const setField = (fieldId: string, value: FormValue) => {
    setForm((prev) => ({ ...prev, [fieldId]: value }));
    setEdited((prev) => new Set(prev).add(fieldId));
  };

  const groupRows = (fieldId: string): GroupRowDraft[] => {
    const value = form[fieldId];
    return Array.isArray(value) ? value : [];
  };
  const setGroupCell = (fieldId: string, rowIdx: number, key: string, v: string) => {
    const rows = [...groupRows(fieldId)];
    rows[rowIdx] = { ...(rows[rowIdx] ?? {}), [key]: v };
    setField(fieldId, rows);
  };
  const addGroupRow = (field: PassportField) => {
    setField(field.id, [...groupRows(field.id), groupRowDraft(field.itemFields ?? [], {})]);
  };
  const removeGroupRow = (fieldId: string, rowIdx: number) => {
    setField(
      fieldId,
      groupRows(fieldId).filter((_, i) => i !== rowIdx),
    );
  };

  /** Rebuild the attrs bag: blanks are omitted (empty string ≠ provided). */
  const buildAttrs = (): Record<string, unknown> => {
    const attrs: Record<string, unknown> = {};
    if (!def || !product) return attrs;
    for (const category of def.categories) {
      for (const field of category.fields) {
        const value = form[field.id];
        if (field.type === "group") {
          // Rows convert back per item type; blank cells and all-blank rows are
          // dropped, and a group with no surviving rows stays absent.
          const rows: Record<string, unknown>[] = [];
          for (const draft of Array.isArray(value) ? value : []) {
            const row: Record<string, unknown> = {};
            for (const item of field.itemFields ?? []) {
              const raw = (draft[item.key] ?? "").trim();
              if (raw === "") continue;
              if (item.type === "number" || item.type === "percentage") row[item.key] = Number(raw);
              else if (item.type === "boolean") row[item.key] = raw === "true";
              else row[item.key] = raw;
            }
            if (Object.keys(row).length > 0) rows.push(row);
          }
          if (rows.length > 0) attrs[field.id] = rows;
        } else if (field.type === "boolean") {
          // Untouched-and-absent booleans stay absent so we don't silently
          // "fill" an optional field with false.
          const originallyPresent = product.attrs[field.id] !== undefined;
          if (originallyPresent || edited.has(field.id)) attrs[field.id] = value === true;
        } else if (typeof value === "string" && value.trim() !== "") {
          attrs[field.id] =
            field.type === "number" || field.type === "percentage" ? Number(value) : value.trim();
        }
      }
    }
    return attrs;
  };

  const save = async () => {
    if (!org || !product) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateProduct(org.id, product.id, { attrs: buildAttrs() });
      setProduct({ ...product, ...updated });
      setEdited(new Set());
      toast.success(t("product.saved"));
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  /** One compact control per group column — labels ride as placeholders to keep rows on one line. */
  const renderGroupCell = (field: PassportField, item: GroupItemField, row: GroupRowDraft, idx: number) => {
    const v = row[item.key] ?? "";
    const placeholder = item.labels[lang];
    const onText = (nv: string) => setGroupCell(field.id, idx, item.key, nv);
    switch (item.type) {
      case "number":
      case "percentage":
        return (
          <NumberInput
            key={item.key}
            placeholder={placeholder}
            disabled={!canWrite}
            value={v}
            onChange={onText}
            {...(item.type === "percentage" ? { min: 0, max: 100 } : {})}
            {...(item.unit ? { suffix: item.unit } : {})}
          />
        );
      case "boolean":
        // Three-way select, not a checkbox: an empty cell must stay "not provided".
        return (
          <Select
            key={item.key}
            placeholder={placeholder}
            disabled={!canWrite}
            options={[
              { label: "—", value: "" },
              { label: t("common.yes"), value: "true" },
              { label: t("common.no"), value: "false" },
            ]}
            value={v}
            onChange={onText}
          />
        );
      case "enum":
        return (
          <Select
            key={item.key}
            placeholder={placeholder}
            disabled={!canWrite}
            options={(item.enumValues ?? []).map((e) => ({ label: e, value: e }))}
            value={v}
            onChange={onText}
          />
        );
      case "country":
        return (
          <CountrySelect
            key={item.key}
            placeholder={placeholder}
            allowClear
            disabled={!canWrite}
            value={v}
            onChange={onText}
          />
        );
      case "date":
        return <DatePicker key={item.key} allowClear disabled={!canWrite} value={v} onChange={onText} />;
      default:
        return (
          <Input
            key={item.key}
            placeholder={placeholder}
            disabled={!canWrite}
            value={v}
            onChange={(e) => onText(e.target.value)}
          />
        );
    }
  };

  const renderField = (field: PassportField) => {
    const label = field.labels[lang];
    const help = field.description;
    const value = form[field.id];
    const common = {
      label,
      required: field.required,
      disabled: !canWrite,
      ...(help ? { help } : {}),
    };
    switch (field.type) {
      case "group": {
        const rows = Array.isArray(value) ? value : [];
        const items = field.itemFields ?? [];
        return (
          <Field key={field.id} label={label} required={field.required} {...(help ? { help } : {})}>
            <div className="pd-group">
              <div
                className="pd-group-grid"
                style={{ gridTemplateColumns: `repeat(${items.length}, minmax(150px, 1fr)) 32px` }}
              >
                {items.map((item) => (
                  <div key={item.key} className="pd-group-hcell">
                    {item.labels[lang]}
                    {item.unit ? ` (${item.unit})` : ""}
                    {item.required ? " *" : ""}
                  </div>
                ))}
                <div />
                {rows.map((row, idx) => (
                  <Fragment key={idx}>
                    {items.map((item) => renderGroupCell(field, item, row, idx))}
                    <Button
                      size="sm"
                      variant="subtle"
                      aria-label={t("common.remove")}
                      icon={<Icon name="trash" size={14} />}
                      disabled={!canWrite}
                      onClick={() => removeGroupRow(field.id, idx)}
                    />
                  </Fragment>
                ))}
              </div>
              <div className="pd-group-foot">
                <Button
                  size="sm"
                  icon={<Icon name="plus" size={14} />}
                  disabled={!canWrite}
                  onClick={() => addGroupRow(field)}
                >
                  {t("product.addRow")}
                </Button>
                {rows.length === 0 && (
                  <Text small secondary>
                    {t("product.noRows")}
                  </Text>
                )}
              </div>
            </div>
          </Field>
        );
      }
      case "number":
      case "percentage":
        return (
          <NumberInput
            key={field.id}
            {...common}
            value={typeof value === "string" ? value : ""}
            onChange={(v) => setField(field.id, v)}
            {...(field.type === "percentage" ? { min: 0, max: 100 } : {})}
            {...(field.unit ? { suffix: field.unit } : {})}
          />
        );
      case "boolean":
        // Field-wrapped so the label row lines up with the neighboring inputs in the grid.
        return (
          <Field key={field.id} label={label} required={field.required} {...(help ? { help } : {})}>
            <Switch
              checked={value === true}
              disabled={!canWrite}
              onChange={(e) => setField(field.id, e.target.checked)}
            >
              {value === true ? t("common.yes") : t("common.no")}
            </Switch>
          </Field>
        );
      case "enum":
        return (
          <Select
            key={field.id}
            {...common}
            options={(field.enumValues ?? []).map((v) => ({ label: v, value: v }))}
            value={typeof value === "string" ? value : ""}
            onChange={(v) => setField(field.id, v)}
          />
        );
      case "country":
        return (
          <CountrySelect
            key={field.id}
            {...common}
            allowClear
            placeholder={t("facilities.countryPlaceholder")}
            value={typeof value === "string" ? value : ""}
            onChange={(v) => setField(field.id, v)}
          />
        );
      case "date":
        return (
          <DatePicker
            key={field.id}
            {...common}
            allowClear
            value={typeof value === "string" ? value : ""}
            onChange={(v) => setField(field.id, v)}
          />
        );
      case "url":
        return (
          <Input
            key={field.id}
            {...common}
            placeholder="https://…"
            value={typeof value === "string" ? value : ""}
            onChange={(e) => setField(field.id, e.target.value)}
          />
        );
      default:
        // Summary/prose fields hold consumer-facing paragraphs — give them room.
        if (field.presentation?.kind === "summary" || field.presentation?.kind === "prose") {
          return (
            <Textarea
              key={field.id}
              {...common}
              rows={3}
              value={typeof value === "string" ? value : ""}
              onChange={(e) => setField(field.id, e.target.value)}
            />
          );
        }
        return (
          <Input
            key={field.id}
            {...common}
            {...(field.unit ? { suffix: field.unit } : {})}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => setField(field.id, e.target.value)}
          />
        );
    }
  };

  const missingLabels = useMemo(
    () => (product ? product.missingRequired.map((f) => fieldLabel(def, f, lang)) : []),
    [product, def, lang],
  );

  /** Missing-required counts per schema category — surfaced next to each section title. */
  const missingByCategory = useMemo(() => {
    const counts = new Map<string, number>();
    if (!product || !def) return counts;
    const missing = new Set(product.missingRequired);
    for (const category of def.categories) {
      const n = category.fields.filter((f) => missing.has(f.id)).length;
      if (n > 0) counts.set(category.id, n);
    }
    return counts;
  }, [product, def]);

  if (orgsLoading) return <Spin />;
  if (!org) return <NoOrgState />;
  if (error && !product)
    return <ErrorAlert error={error} onRetry={() => org && id && void load(org.id, id)} />;
  if (!product) return <Spin />;

  const tabItems = [
    // One category per rail tab (schemas carry 4–7) instead of stacked sections —
    // stacking made the data tab scroll for screens. The outer badge keeps the
    // total; each rail entry carries its own missing count. Edits span tabs, so
    // the sticky save bar still saves everything at once.
    ...(def
      ? [
          {
            key: "__data",
            label: (
              <Space size="sm">
                {t("product.data")}
                <Badge count={product.missingRequired.length} />
              </Space>
            ),
            children: (
              <div style={{ paddingTop: "var(--az-sp-3)" }}>
                <Tabs
                  orientation="vertical"
                  items={def.categories.map((category) => {
                    const missing = missingByCategory.get(category.id) ?? 0;
                    return {
                      key: category.id,
                      label: (
                        <span className="pd-cat-tab">
                          {category.labels[lang]}
                          <Badge count={missing} />
                        </span>
                      ),
                      children: (
                        <section className="pd-section">
                          <div className="pd-section-head">
                            <Title level={5}>{category.labels[lang]}</Title>
                            {missing > 0 && (
                              <Tag color="warning">
                                {missing} {t("product.missingShort")}
                              </Tag>
                            )}
                          </div>
                          <div className="pd-field-grid">
                            {category.fields
                              // A superseded field stays only while it still holds data; new
                              // entries go to its successor (the common compliance category).
                              .filter((field) => !field.deprecatedBy || hasFormValue(form[field.id]))
                              .map((field) => (
                                <div key={field.id} className={isWideField(field) ? "pd-span2" : undefined}>
                                  {renderField(field)}
                                </div>
                              ))}
                          </div>
                        </section>
                      ),
                    };
                  })}
                />
              </div>
            ),
          },
        ]
      : []),
    {
      key: "__identifiers",
      label: t("product.identifiers"),
      children: (
        <Space direction="vertical" size="md" style={{ width: "100%", paddingTop: "var(--az-sp-3)" }}>
          <ProductIdentifiersPanel
            orgId={org.id}
            product={product}
            canWrite={canWrite}
            onChanged={() => void load(org.id, product.id)}
            onError={setError}
          />
          {org && <ProductOperatorsCard orgId={org.id} productId={product.id} canWrite={canWrite} />}
        </Space>
      ),
    },
    {
      key: "__chain",
      label: t("chain.title"),
      children: (
        <div style={{ paddingTop: "var(--az-sp-3)" }}>
          <SupplyChainCard orgId={org.id} productId={product.id} canWrite={canWrite} />
        </div>
      ),
    },
    {
      key: "__components",
      label: t("comps.title"),
      children: (
        <div style={{ paddingTop: "var(--az-sp-3)" }}>
          <ComponentsCard orgId={org.id} productId={product.id} canWrite={canWrite} />
        </div>
      ),
    },
    {
      key: "__lca",
      label: t("lca.title"),
      children: (
        <div style={{ paddingTop: "var(--az-sp-3)" }}>
          <LcaCard orgId={org.id} productId={product.id} canWrite={canWrite} />
        </div>
      ),
    },
    {
      key: "__media",
      label: t("media.title"),
      children: (
        <div style={{ paddingTop: "var(--az-sp-3)" }}>
          <MediaCard orgId={org.id} productId={product.id} canWrite={canWrite} />
        </div>
      ),
    },
    {
      key: "__documents",
      label: t("documents.title"),
      children: (
        <div style={{ paddingTop: "var(--az-sp-3)" }}>
          <DocumentsCard orgId={org.id} productId={product.id} canWrite={canWrite} />
        </div>
      ),
    },
    ...(canManageSuppliers
      ? [
          {
            key: "__suppliers",
            label: t("suppliers.title"),
            children: (
              <div style={{ paddingTop: "var(--az-sp-3)" }}>
                <SupplierInvitesCard
                  orgId={org.id}
                  productId={product.id}
                  def={def}
                  onApplied={() => void load(org.id, product.id)}
                />
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <Space direction="vertical" size="lg" style={{ width: "100%" }}>
        <Card
          title={`${product.sku} — ${product.name}`}
          extra={<Tag color={STATUS_COLOR[product.status]}>{t(`productStatus.${product.status}`)}</Tag>}
        >
          {error != null && <ErrorAlert error={error} />}
          <div className="pd-head">
            <div>
              {/* SKU and name are the card title already — this grid carries only
                what the title cannot: the registry identity and the timestamps. */}
              <Descriptions
                column={3}
                items={[
                  { label: t("products.gtin"), children: product.gtin ?? "—" },
                  { label: t("products.schema"), children: def ? def.title[lang] : product.schemaId },
                  { label: t("product.identifiers"), children: product.identifiers.length },
                  { label: t("common.createdAt"), children: fmtDate(product.createdAt) },
                  { label: t("common.updatedAt"), children: fmtDate(product.updatedAt) },
                ]}
              />
              <ProductCustomsCodes
                orgId={org.id}
                product={product}
                canWrite={canWrite}
                onUpdated={(updated) => setProduct({ ...product, ...updated })}
                onError={setError}
              />
            </div>

            {/* Compliance is the number this page exists for, so it takes the column
              the lone progress bar used to leave empty below itself — and absorbs
              the missing-required list, which was a third copy of the same warning
              (the data tab carries a badge and per-section counts too). */}
            <div className="pd-compliance">
              <Text small secondary>
                {t("product.compliance")}
              </Text>
              <div className="pd-compliance-value">{product.complianceScore}%</div>
              <Progress
                percent={product.complianceScore}
                status={product.complianceScore === 100 ? "success" : "normal"}
                showInfo={false}
              />
              {missingLabels.length === 0 ? (
                <Text small secondary>
                  {t("product.complete")}
                </Text>
              ) : (
                <div className="pd-compliance-missing">
                  <Text small>
                    {t("product.missingRequired")} ({missingLabels.length})
                  </Text>
                  <Text small secondary>
                    {missingLabels.slice(0, 4).join(", ")}
                    {missingLabels.length > 4 && ` +${missingLabels.length - 4}`}
                  </Text>
                </div>
              )}
              {product.readinessIssues.length > 0 && (
                <div className="pd-compliance-missing">
                  <Text small>{t("readiness.title")}</Text>
                  {product.readinessIssues.map((issue, i) => (
                    <Text small secondary key={`${issue.rule}-${i}`}>
                      <Tag color={issue.severity === "block" ? "error" : "warning"}>
                        {t(`readiness.${issue.severity}`)}
                      </Tag>{" "}
                      {issue.message[lang]}
                    </Text>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Card>

        {!canWrite && <Alert type="info">{t("product.readOnly")}</Alert>}

        {!def && <Alert type="warning">{t("product.unknownSchema")}</Alert>}

        <Card>
          <Tabs variant="card" items={tabItems} className="pd-tabs" />
        </Card>
      </Space>

      {/* Sticky save bar — data-entry edits save from here, always in view. */}
      {canWrite && def && edited.size > 0 && (
        <div className="pd-savebar">
          <Text small secondary>
            {edited.size} {t("product.editedCount")}
          </Text>
          <Space size="sm">
            <Button size="sm" disabled={saving} onClick={() => initForm(product)}>
              {t("product.discard")}
            </Button>
            <Button size="sm" variant="primary" loading={saving} onClick={() => void save()}>
              {t("common.save")}
            </Button>
          </Space>
        </div>
      )}
    </>
  );
}
